import { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import iAugmentlogo from "../../assets/image/logo.png";
import { logout } from "../../features/Auth/AuthSlice";
import {
  MdMenu,
  MdLogout,
  MdAutoAwesome,
  MdPalette,
  MdCompare,
  MdTableChart,
  MdHourglassEmpty,
  MdExpandMore,
  MdChevronRight,
  MdPeople,
  MdCheckroom,
  MdAutoFixHigh,
  MdInventory2,
  MdLandscape,
  MdCategory,
} from "react-icons/md";

// ===== STUDIO ASSETS NAVIGATION (ADMIN & SUPERADMIN) =====
const STUDIO_ASSETS_NAVIGATION_ITEM = {
  label: "Studio Assets",
  icon: MdInventory2,
  subItems: [
    { path: "/studio-assets/products", label: "Products", icon: MdCheckroom },
    { path: "/studio-assets/backgrounds", label: "Backgrounds", icon: MdLandscape },
    { path: "/studio-assets/models", label: "Models", icon: MdPeople },
    { path: "/studio-assets/categories", label: "Categories", icon: MdCategory },
  ],
};

// ===== SUPERADMIN NAVIGATION CONFIGURATION =====
const SUPERADMIN_NAVIGATION_ITEMS = [
  {
    label: "Show & Tell",
    icon: MdAutoAwesome,
    subItems: [
      { path: "/show-tell/model-to-model", label: "Model to Model", icon: MdPeople },
      { path: "/show-tell/mannequin-to-model", label: "Mannequin to Model", icon: MdCheckroom },
      { path: "/show-tell/virtual-try-on", label: "Virtual Try-On", icon: MdAutoFixHigh },
    ],
  },
  { path: "/playground", icon: MdPalette, label: "Playground" },
  { path: "/comparison", icon: MdCompare, label: "Comparison" },
  {
    path: "/calculation-sheet",
    icon: MdTableChart,
    label: "Calculation Sheet",
  },
  { path: "/queue", icon: MdHourglassEmpty, label: "Queue" },
];

// ===== STANDARD NAVIGATION CONFIGURATION (ADMIN & USER) =====
const STANDARD_NAVIGATION_ITEMS = [
  {
    label: "Show & Tell",
    icon: MdAutoAwesome,
    subItems: [
      { path: "/show-tell/model-to-model", label: "Model to Model", icon: MdPeople },
      { path: "/show-tell/mannequin-to-model", label: "Mannequin to Model", icon: MdCheckroom },
      { path: "/show-tell/virtual-try-on", label: "Virtual Try-On", icon: MdAutoFixHigh },
    ],
  },
  { path: "/playground", icon: MdPalette, label: "Playground" },
  { path: "/queue", icon: MdHourglassEmpty, label: "Queue" },
];

const Sidebar = ({ isClosed, toggleSidebar, isMobile }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role: userRole } = useSelector((state) => state.auth);

  const isAdminUser = userRole === "admin" || userRole === "superadmin";

  const navigationItems = useMemo(
    () =>
      userRole === "superadmin"
        ? [STUDIO_ASSETS_NAVIGATION_ITEM, ...SUPERADMIN_NAVIGATION_ITEMS]
        : isAdminUser
          ? [STUDIO_ASSETS_NAVIGATION_ITEM, ...STANDARD_NAVIGATION_ITEMS]
          : STANDARD_NAVIGATION_ITEMS,
    [userRole, isAdminUser]
  );

  const [expandedMenus, setExpandedMenus] = useState({
    "Studio Assets": true,
    "Show & Tell": true,
  });

  const toggleMenu = (label) => {
    setExpandedMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (path) => location.pathname === path;

  const hasActiveSubItem = (item) => {
    if (!item.subItems) return false;
    return item.subItems.some((sub) => location.pathname === sub.path);
  };

  useEffect(() => {
    navigationItems.forEach((item) => {
      if (item.subItems) {
        const hasActiveSub = item.subItems.some((sub) => location.pathname === sub.path);
        if (hasActiveSub) {
          setExpandedMenus((prev) => ({ ...prev, [item.label]: true }));
        }
      }
    });
  }, [location.pathname, navigationItems]);

  const navLinkClass = (path) => {
    const active = isActive(path);
    return `app-sidebar-link flex items-center rounded-lg transition-colors duration-200 text-xs font-semibold focus:outline-none ${
      isClosed ? "justify-center px-0 py-2 w-full" : "gap-3 px-3 py-2"
    } ${
      active
        ? "bg-surface text-primary border border-outline shadow-sm active font-bold"
        : "text-secondary hover:text-primary hover:bg-surface-container"
    }`;
  };

  const subNavLinkClass = (path) => {
    const active = isActive(path);
    return `app-sidebar-link flex items-center rounded-lg transition-colors duration-200 text-xs font-semibold focus:outline-none w-full px-3 py-2 ${
      active
        ? "bg-surface text-primary border border-outline shadow-sm active font-bold"
        : "text-secondary hover:text-primary hover:bg-surface-container"
    }`;
  };

  const parentLinkClass = (item) => {
    const active = hasActiveSubItem(item);
    return `app-sidebar-link flex items-center rounded-lg transition-colors duration-200 text-xs font-semibold focus:outline-none cursor-pointer w-full text-left ${
      isClosed ? "justify-center px-0 py-2" : "justify-between px-3 py-2"
    } ${
      active
        ? "bg-surface text-primary border border-outline shadow-sm active font-bold"
        : "text-secondary hover:text-primary hover:bg-surface-container"
    }`;
  };

  const handleLogout = (e) => {
    e?.preventDefault();
    dispatch(logout());
    navigate("/login");
  };

  const navClass = `app-sidebar flex h-screen min-h-0 flex-col z-[100] transition-all duration-300 shrink-0 fixed lg:sticky top-0 left-0 ${
    isMobile
      ? isClosed
        ? "-translate-x-full w-full sm:w-72 sm:max-w-[85vw]"
        : "translate-x-0 w-full sm:w-72 sm:max-w-[85vw] shadow-2xl"
      : `lg:translate-x-0 ${isClosed ? "lg:w-16" : "lg:w-56"}`
  }`;

  return (
    <>
      {isMobile && !isClosed && (
        <div
          onClick={toggleSidebar}
          className="fixed inset-0 bg-black/40 z-[90] backdrop-blur-xs transition-opacity lg:hidden"
          aria-hidden="true"
        />
      )}
      <nav className={navClass}>
        {/* BRAND */}
        <div className={`mb-0 py-3 flex items-center border-b border-outline/50 h-14 shrink-0 logo-container ${
          isClosed ? "justify-center px-0" : "justify-between px-4"
        }`}>
          {!isClosed && (
            <div>
              <img
                src={iAugmentlogo}
                alt="iAugment Logo"
                className="h-9 w-32 object-contain"
              />
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="text-secondary hover:text-primary p-1 hover:bg-surface-container rounded-lg transition-all cursor-pointer focus:outline-none border-none bg-transparent flex items-center justify-center"
            type="button"
          >
            <MdMenu size={22} />
          </button>
        </div>

        {/* NAV */}
        <nav className={`custom-scrollbar flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto ${
          isClosed ? "px-1.5" : "px-2"
        } pt-4`}>
          {navigationItems.map((item) => {
            if (item.subItems) {
              const IconComponent = item.icon;
              const isExpanded = expandedMenus[item.label];

              return (
                <div key={item.label} className="flex flex-col gap-1 w-full items-center">
                  <button
                    onClick={() => toggleMenu(item.label)}
                    className={`${parentLinkClass(item)} relative group`}
                    type="button"
                    title={isClosed ? item.label : undefined}
                  >
                    <div className="flex items-center gap-3">
                      <IconComponent size={20} className="shrink-0" />
                      {!isClosed && (
                        <span className="sidebar-text text-left">{item.label}</span>
                      )}
                    </div>
                    {!isClosed && (
                      <div
                        className={`shrink-0 ${
                          hasActiveSubItem(item) ? "text-primary" : "text-secondary"
                        }`}
                      >
                        {isExpanded ? (
                          <MdExpandMore size={18} />
                        ) : (
                          <MdChevronRight size={18} />
                        )}
                      </div>
                    )}
                    {/* Tooltip on hover when collapsed */}
                    {isClosed && (
                      <span className="pointer-events-none absolute left-full ml-2.5 px-2.5 py-1 bg-slate-900 text-white text-[11px] font-medium rounded-md shadow-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-[110]">
                        {item.label}
                      </span>
                    )}
                  </button>

                  {/* Render nested items when OPEN */}
                  {!isClosed && isExpanded && (
                    <div className="ml-[18px] pl-1 border-l border-outline/50 flex flex-col gap-1 mr-1 animate-fade-in w-[calc(100%-26px)]">
                      {item.subItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const active = isActive(subItem.path);
                        return (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => isMobile && toggleSidebar()}
                            className={`${subNavLinkClass(subItem.path)} gap-2.5`}
                          >
                            {SubIcon && (
                              <SubIcon
                                size={16}
                                className={`shrink-0 transition-colors ${
                                  active ? "text-primary" : "text-secondary"
                                }`}
                              />
                            )}
                            <span className="sidebar-text truncate">{subItem.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}

                  {/* Render nested items when CLOSED with vertical hierarchy line */}
                  {isClosed && isExpanded && (
                    <div className="relative flex flex-col w-full py-1 animate-fade-in">
                      {/* Vertical hierarchy connector line */}
                      <div
                        className="absolute top-1 bottom-1 w-[1.5px] bg-slate-300 rounded-full"
                        style={{ left: "16px" }}
                      />
                      {/* Nested sub-items indented to the right of the hierarchy line */}
                      <div className="flex flex-col gap-1.5 w-full" style={{ paddingLeft: "21px" }}>
                        {item.subItems.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const active = isActive(subItem.path);
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              title={subItem.label}
                              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all relative group ${
                                active
                                  ? "bg-surface text-primary border border-outline shadow-sm font-bold"
                                  : "text-secondary hover:text-primary hover:bg-surface-container"
                              }`}
                            >
                              {SubIcon && <SubIcon size={16} className="shrink-0" />}
                              {/* Floating tooltip on hover */}
                              <span className="pointer-events-none absolute left-full ml-2.5 px-2.5 py-1 bg-slate-900 text-white text-[11px] font-medium rounded-md shadow-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-[110]">
                                {subItem.label}
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            const IconComponent = item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => isMobile && toggleSidebar()}
                className={navLinkClass(item.path)}
              >
                <IconComponent size={20} className="shrink-0" />
                {!isClosed && (
                  <span className="sidebar-text">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* LOGOUT */}
        <div className={`mt-auto shrink-0 border-t border-outline h-12 flex items-center justify-center ${
          isClosed ? "px-2" : "px-4"
        }`}>
          <button
            onClick={handleLogout}
            className={`flex items-center text-red-500 hover:bg-red-50 cursor-pointer rounded-lg transition-colors duration-200 logout-btn font-semibold text-xs border-none bg-transparent py-1.5 ${
              isClosed ? "justify-center w-full" : "gap-3 w-full"
            }`}
            type="button"
          >
            <MdLogout size={20} className="shrink-0" />
            {!isClosed && <span className="sidebar-text">Logout</span>}
          </button>
        </div>
      </nav>
    </>
  );
};

export default Sidebar;
